import { useState } from "react";
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCachedData, useCacheInvalidationListener } from '../utils/useCache';

// Helper to resolve an image source from an event object
function resolveImageSrc(item, apiBase) {
  if (!item) return null;
  
  // prefer preloaded object URL
  if (item._imageSrc) return item._imageSrc;

  // image_data may be a data: URL string, a base64 string, or a Buffer-like object
  if (item.image_data) {
    const d = item.image_data;
    if (typeof d === 'string') {
      const trimmed = d.trim();
      if (trimmed) {
        if (trimmed.startsWith('data:')) return trimmed;
        // assume base64 string -> try to wrap with mimetype if available
        const mime = item.image_mimetype || 'image/jpeg';
        return `data:${mime};base64,${trimmed}`;
      }
    }
    if (typeof d === 'object') {
      try {
        const arr = Array.isArray(d.data) ? new Uint8Array(d.data) : new Uint8Array(d);
        const blob = new Blob([arr], { type: item.image_mimetype || 'image/jpeg' });
        return URL.createObjectURL(blob);
      } catch (err) {
        console.warn('Failed to convert image_data to blob:', err);
        // fallthrough to image_url
      }
    }
  }

  // image_url handling - validate it's not empty/invalid
  if (item.image_url) {
    const imageUrl = String(item.image_url).trim();
    
    // Skip empty or invalid URLs
    if (!imageUrl || imageUrl === 'null' || imageUrl === 'undefined') return null;
    
    // absolute URL starting with http(s)
    if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
    
    // URLs starting with / - these are either API endpoints or absolute paths
    // Check if it looks like an API endpoint (contains /api/)
    if (imageUrl.startsWith('/api/')) {
      // It's an API endpoint, prefix with apiBase
      return `${apiBase}${imageUrl}`;
    }
    
    // Other absolute paths starting with /
    if (imageUrl.startsWith('/')) {
      return `${apiBase}${imageUrl}`;
    }
    
    // numeric id mapping (Admin used this pattern)
    if (!isNaN(Number(imageUrl))) {
      return `${apiBase}/image/${imageUrl}`;
    }
    
    // relative path - prefix with apiBase
    if (imageUrl.length > 0) {
      return `${apiBase}/${imageUrl}`;
    }
  }

  return null;
}

function Events() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';
  const { data: events, loading, error, refetch } = useCachedData(
    '/api/events',
    () => axios.get(`${API_BASE}/api/events`).then(res => res.data),
    300000 // 5 minute cache
  );
  
  // Listen for cache invalidation events from admin pages and refetch
  useCacheInvalidationListener('/api/events', refetch);
  
  const navigate = useNavigate();

  // Event icons mapping
  const getEventIcon = (category, index) => {
    const icons = {
      'conference': '🎤',
      'workshop': '🛠️',
      'webinar': '💻',
      'meetup': '🤝',
      'competition': '🏆',
      'seminar': '📚',
      'default': ['📅', '🎉', '🗓️', '🎈', '🎟️', '🏅', '🧑‍💼', '🌐'][index % 8]
    };
    const categoryKey = category ? category.toLowerCase() : 'default';
    return icons[categoryKey] || icons['default'];
  };

  // Card animation variants (copied from Services)
  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" }
    })
  };

  return (
    <div className="w-full max-w-8xl rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-2 sm:p-4 md:p-8 text-base sm:text-lg overflow-x-auto" style={{ background: 'rgba(245, 247, 255, 0.85)', backdropFilter: 'blur(16px)' }}>
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 bg-slate-50" />

        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto container">
            {/* Main Title */}
            <motion.h1 
              className="text-5xl font-extrabold mb-12 text-center text-black tracking-wide" 
              style={{textShadow: '0 6px 32px rgba(0,0,0,0.45)'}}
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              Events & <span className="text-primary-500">Conferences</span>
            </motion.h1>

        {/* Loading State */}
        {loading && (
          <motion.div 
            className="text-center mb-16 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <LoadingSpinner message="Loading events..." bgColor="bg-transparent" spinnerColor="border-[#73ab40]" py={0} />
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center px-6 py-3 bg-blue-50 rounded-full shadow-lg border border-blue-200">
              <p className="text-blue-600 font-medium">No events available right now. Check back soon!</p>
            </div>
          </motion.div>
        )}

        {/* Events Grid */}
        {!loading && !error && events.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 items-stretch">
            {events.sort((a, b) => {
              const dateA = new Date(a.date || 0);
              const dateB = new Date(b.date || 0);
              return dateA - dateB;
            }).map((event, idx) => {
              const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/backend/php';
              const imageSource = resolveImageSrc(event, API_BASE_URL);
              console.log(`Event ${event.id} (${event.title}):`, { image_data: event.image_data ? 'exists' : 'none', image_url: event.image_url, imageSource });
              
              return (
              <button
                key={event.id || idx}
                onClick={() => navigate(`/events/${event.id}`)}
                className="w-full group text-left"
              >
                <motion.div
                  className={`relative bg-white backdrop-blur-xl rounded-3xl pt-6 px-6 pb-6 flex flex-col items-center text-center shadow-medium border-2 border-slate-200 hover:border-primary-400/60 transition-all duration-500 h-full cursor-pointer group-hover:shadow-strong`}
                  custom={idx}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                  whileHover={{
                    y: -20,
                    scale: 1.05,
                    boxShadow: "0 20px 60px rgba(15, 23, 42, 0.15)"
                  }}
                >
                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-primary-500/15 to-transparent rounded-bl-3xl rounded-tr-3xl"></div>

                  {/* Event image - only load if valid source exists */}
                  <div className="relative w-full overflow-hidden mb-4 rounded-xl" style={{ paddingBottom: '60%' }}>
                    <img
                      src={imageSource || '/images/image(1).png'}
                      alt={event.title || event.name}
                      className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => { 
                        // Only set default if current src is not already the default
                        if (!e.currentTarget.src.includes('image(1).png')) {
                          e.currentTarget.src = '/images/image(1).png';
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  </div>

                  <div className={`w-full flex flex-col items-center text-center`}>
                    {/* Event Title */}
                    <h2 className="text-xl font-bold text-slate-800 mb-3 drop-shadow-sm group-hover:text-primary-600 transition-colors duration-300">
                      {event.title && event.title.length > 50 
                        ? event.title.slice(0, 50) + '...' 
                        : event.title}
                    </h2>

                    {/* Event Date */}
                    {event.date && (
                      <div className="text-slate-600 text-sm mb-4 font-medium">
                        {event.date}
                      </div>
                    )}

                    {/* Event Description */}
                    <div className="text-slate-600 text-sm flex-grow">
                      <SafeRichText content={event.description 
                        ? event.description.slice(0, 120) + '...'
                        : (event.content ? event.content.slice(0, 120) + '...' : '')} />
                    </div>

                    {/* Learn more indicator */}
                    <motion.div 
                      className="mt-4 text-primary-600 font-medium text-base opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      initial={{ y: 10 }}
                      whileHover={{ y: 0 }}
                    >
                      Learn more →
                    </motion.div>
                  </div>
                </motion.div>
              </button>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && events.length === 0 && (
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/10 rounded-2xl shadow-lg border border-white/10 p-8 max-w-md mx-auto">
              <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/40 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl">🎫</span>
              </div>
              <h3 className="text-2xl font-bold text-black mb-2">No Events Yet</h3>
              <p className="text-black">Events will be displayed here once they are added.</p>
            </div>
          </motion.div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Events;
